import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { fmt } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Pencil, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function Clientes() {
  const { isAdmin, isOpDetalle, isReadOnly } = useAuth();
  const canCreateClients = isAdmin || isOpDetalle;
  const canExportClients = isAdmin || isReadOnly;
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [referrers, setReferrers] = useState([]);
  const [filter, setFilter] = useState("todos");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", referrer_id: "", note: "", status: "active" });

  const load = async () => {
    const [c, s, k, r] = await Promise.all([
      api.get("/clients"),
      api.get("/clients/stats"),
      api.get("/clients/kpis").catch(() => ({ data: null })),
      api.get("/referrers"),
    ]);
    setClients(c.data); setStats(s.data); setKpis(k.data); setReferrers(r.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ __new: true }); setForm({ name: "", phone: "", referrer_id: "", note: "", status: "active" }); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, phone: c.phone || "", referrer_id: c.referrer_id || "", note: c.note || "", status: c.status || "active" }); };

  const save = async () => {
    try {
      const payload = { ...form, referrer_id: form.referrer_id || null };
      if (editing && editing.id) await api.put(`/clients/${editing.id}`, payload);
      else await api.post("/clients", payload);
      toast.success("Cliente guardado");
      setEditing(null); setForm({ name: "", phone: "", referrer_id: "", note: "", status: "active" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const filtered = useMemo(() => {
    let arr = stats;
    if (filter === "activos") arr = arr.filter((s) => s.total_usdt > 0);
    if (filter === "inactivos") arr = arr.filter((s) => s.total_usdt === 0);
    return arr;
  }, [stats, filter]);

  const chartData = filtered.slice(0, 12).map((s) => ({ name: s.client_name.slice(0, 14), usdt: s.total_usdt }));

  const exportCsv = async () => {
    const token = localStorage.getItem("token") || "";
    const res = await fetch(`${API}/reports/clients`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `clientes.csv`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="clientes-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Clientes</h1>
          <p className="text-slate-500 mt-1">Actividad desde el día 1 del mes actual</p>
        </div>
        <div className="flex gap-2">
          {canExportClients && <Button variant="outline" onClick={exportCsv} data-testid="export-clients"><Download size={16} className="mr-2" /> Exportar CSV</Button>}
          {canCreateClients && <Button onClick={openNew} className="bg-slate-900" data-testid="new-client-btn"><UserPlus size={16} className="mr-2" /> Nuevo Cliente</Button>}
        </div>
      </div>

      {kpis && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            ["Activos 3m", kpis.active_3m, "green"],
            ["Activos 6m", kpis.active_6m, "blue"],
            ["Activos 12m", kpis.active_12m, "slate"],
            ["Inactivos 3m", kpis.inactive_3m, "red"],
            ["Total clientes", kpis.total_clients, "slate"],
            ["Ventas del mes", fmt.usdt(kpis.month_total_usdt) + " USDT", "green"],
            ["Operaciones del mes", kpis.month_operations, "slate"],
            ["Promedio/cliente", fmt.usdt(kpis.month_avg_per_client), "blue"],
          ].map(([label, value, color]) => (
            <Card key={label} className="p-4 border border-slate-200 rounded-xl bg-white">
              <div className="text-xs uppercase font-semibold text-slate-500">{label}</div>
              <div className={`font-mono tabular-nums text-xl font-bold mt-1 ${color === "green" ? "text-emerald-600" : color === "red" ? "text-rose-600" : color === "blue" ? "text-sky-600" : "text-slate-900"}`}>{value}</div>
            </Card>
          ))}
        </div>
      )}

      <Card className="p-5 border border-slate-200 rounded-xl bg-white">
        <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider mb-4">Top 12 clientes por USDT comprado (mes actual)</h3>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} angle={-30} textAnchor="end" />
              <YAxis tick={{ fontSize: 11, fill: "#475569" }} />
              <Tooltip />
              <Bar dataKey="usdt" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="flex items-center gap-3">
        <Label className="text-xs uppercase font-semibold text-slate-500">Filtro</Label>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-56" data-testid="clients-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="activos">Activos (con compras en el mes)</SelectItem>
            <SelectItem value="inactivos">Sin compras este mes</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border border-slate-200 rounded-xl bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="clients-table">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Cliente</th>
                <th className="px-4 py-3 text-left font-semibold">Referidor</th>
                <th className="px-4 py-3 text-right font-semibold">USDT mes</th>
                <th className="px-4 py-3 text-right font-semibold">Ops</th>
                <th className="px-4 py-3 text-left font-semibold">Última compra</th>
                <th className="px-4 py-3 text-left font-semibold">Estado</th>
                <th className="px-4 py-3 text-right font-semibold"></th>
              </tr>
            </thead>
            <tbody className="font-mono tabular-nums">
              {filtered.length === 0 && (<tr><td colSpan={7} className="text-center py-8 text-slate-400">Sin clientes</td></tr>)}
              {filtered.map((s) => {
                const c = clients.find((x) => x.id === s.client_id);
                return (
                  <tr key={s.client_id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-semibold font-sans text-slate-800">{s.client_name}</td>
                    <td className="px-4 py-3 font-sans text-emerald-700">{s.referrer_name || "—"}</td>
                    <td className="px-4 py-3 text-right">{fmt.usdt(s.total_usdt)}</td>
                    <td className="px-4 py-3 text-right">{s.operations}</td>
                    <td className="px-4 py-3 text-slate-600">{s.last_purchase_date ? fmt.date(s.last_purchase_date) : "—"}</td>
                    <td className="px-4 py-3"><Badge className={s.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}>{s.status}</Badge></td>
                    <td className="px-4 py-3 text-right">
                      {isAdmin && c && (<button onClick={() => openEdit(c)} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" data-testid={`edit-client-${c.id}`}><Pencil size={14} /></button>)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="client-edit-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Editar" : "Nuevo"} cliente</DialogTitle></DialogHeader>
          <ClientForm form={form} setForm={setForm} referrers={referrers} clients={clients} currentClientId={editing?.id} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={save} className="bg-slate-900" data-testid="save-client">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ClientForm({ form, setForm, referrers, clients, currentClientId }) {
  const clientReferrers = clients.filter((c) => c.id !== currentClientId && c.status !== "inactive");
  const externalReferrers = referrers.filter((r) => r.referrer_type !== "client");

  return (
    <div className="space-y-3">
      <div><Label>Nombre *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name" /></div>
      <div><Label>Teléfono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
      <div>
        <Label>Referidor</Label>
        <Select value={form.referrer_id || "__none__"} onValueChange={(v) => setForm({ ...form, referrer_id: v === "__none__" ? "" : v })}>
          <SelectTrigger data-testid="client-referrer"><SelectValue placeholder="— Ninguno —" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— Ninguno —</SelectItem>
            {clientReferrers.map((c) => (<SelectItem key={`client-${c.id}`} value={c.id}>Cliente: {c.name}</SelectItem>))}
            {externalReferrers.map((r) => (<SelectItem key={`referrer-${r.id}`} value={r.id}>Referidor externo: {r.name}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>
      <div><Label>Nota</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="client-note" /></div>
      <div>
        <Label>Estado</Label>
        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="active">Activo</SelectItem><SelectItem value="inactive">Inactivo</SelectItem></SelectContent>
        </Select>
      </div>
    </div>
  );
}

function NewClientDialog() { return null; }
